import { useMemo, useState } from 'react';
import { format, startOfWeek, startOfMonth, addDays, addWeeks, addMonths } from 'date-fns';
import { useLocale } from '../context/LocaleContext';

// Which slice of the calendar is on screen: the view, the week sub-mode, where
// the cursor sits, and the date window that falls out of them.
//
// The window is padded for the month grid so its leading and trailing days are
// populated — a month view that queries only the month has empty corners.
//
// The week begins on the day the studio's Region begins one (#505): MMS's
// month view runs Monday to Sunday for United Kingdom and Sunday to Saturday
// for United States, and the rule is the core's `first_weekday`. `weekStart`
// is handed to the week views so no view can start a week on another day.

const iso = (d) => format(d, 'yyyy-MM-dd');

export const WEEK_MODES = { agenda: 'Agenda', list: 'List' };
export const DEFAULT_WEEK_MODE = 'agenda';

export default function useCalendarView() {
  const [view, setView] = useState('month');
  const [weekMode, setWeekMode] = useState(DEFAULT_WEEK_MODE);
  const [cursor, setCursor] = useState(() => new Date());
  const { weekStartsOn, formatLongDate, formatMonth } = useLocale();
  const weekStart = useMemo(() => startOfWeek(cursor, { weekStartsOn }), [cursor, weekStartsOn]);

  const [rangeStart, rangeEnd] = useMemo(() => {
    if (view === 'day') return [iso(cursor), iso(cursor)];
    if (view === 'week') return [iso(weekStart), iso(addDays(weekStart, 6))];
    const s = startOfWeek(startOfMonth(cursor), { weekStartsOn });
    return [iso(s), iso(addDays(s, 41))];
  }, [view, cursor, weekStart, weekStartsOn]);

  // MMS titles both week modes with the selected day, as the day view does,
  // rather than naming the mode or the week's first day (#552): the arrows
  // move the selected day by seven, as `step` does, and switching to Day keeps
  // the day, since all three read the one cursor.
  //
  // In the Region's words (#534): the locale's full date, `Wednesday,
  // September 30, 2026` in the United States and `Mittwoch, 30. September
  // 2026` in Germany, as MMS writes it; the month's name and year likewise.
  const title = view === 'day' || view === 'week'
    ? formatLongDate(iso(cursor))
    : formatMonth(format(cursor, 'yyyy-MM'));

  const step = (dir) => setCursor((c) => {
    if (view === 'day') return addDays(c, dir);
    if (view === 'week') return addWeeks(c, dir);
    return addMonths(c, dir);
  });

  const today = () => setCursor(new Date());

  return {
    view, setView, weekMode, setWeekMode,
    cursor, setCursor, weekStart, rangeStart, rangeEnd, title, step, today,
  };
}
